import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  RadioInputDiv,
  RadioWrapper,
  ActionButtonsRow,
  Result,
  ReferenceCard,
  MathDiv,
  Select,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyImageButton,
  ImageDiv,
  SvgImage,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { FaRegCopy } from 'react-icons/fa6'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'
import { Table, Th, Td, Tr, TableContainer } from './styles'
export function ConcreteBending() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [tShape, setTShape] = useState(false)
  const [b, setB] = useState('')
  const [bf, setBf] = useState('')
  const [bw, setBw] = useState('')
  const [h, setH] = useState('')
  const [hf, setHf] = useState('')
  const [dlb, setDlb] = useState('')
  const [dlt, setDlt] = useState('')
  const [mk, setMk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [rhoMin, setRhoMin] = useState(0.15)
  const [distribution, setDistribution] = useState('beam')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-bending-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setTShape(entry.tShape || false)
      setB(entry.b)
      setBf(entry.bf)
      setBw(entry.bw)
      setH(entry.h)
      setHf(entry.hf)
      setDlb(entry.dlb)
      setDlt(entry.dlt)
      setMk(entry.mk || entry.md || '')
      setGammaf(entry.gammaf || 1.4)
      setRhoMin(entry.rhoMin)
      setDistribution(entry.distribution || 'beam')
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'rhoMin' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    if (name === 'fck' && numValue > 50) {
      return 'fck deve ser menor ou igual a 50 MPa'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.h = validateField('h', h)
    if (tShape) {
      newErrors.bf = validateField('bf', bf)
      newErrors.bw = validateField('bw', bw)
      newErrors.hf = validateField('hf', hf)
    } else {
      newErrors.b = validateField('b', b)
    }
    newErrors.dlb = validateField('dlb', dlb)
    newErrors.dlt = validateField('dlt', dlt)
    newErrors.mk = validateField('mk', mk)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.rhoMin = validateField('rhoMin', rhoMin)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [b, bf, bw, h, hf, tShape, dlb, dlt, mk, gammaf, fck, fyk, rhoMin, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  const convertToStandardUnits = () => {
    const base = {
      fck,
      fyk,
      h,
      dlb,
      dlt,
      md: mk * gammaf,
      distribution,
      tShape,
    }

    if (tShape) {
      return { ...base, bw, bf, hf }
    }

    return { ...base, b }
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const convertedValues = convertToStandardUnits()
      const data = {
        ...convertedValues,
        rhoMin,
      }

      setData(data)

      const response = await api.post('/concrete/bending', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        tShape,
        b,
        bf,
        bw,
        h,
        hf,
        dlb,
        dlt,
        mk,
        gammaf,
        rhoMin,
        distribution,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      tShape,
      b,
      bf,
      bw,
      h,
      hf,
      dlb,
      dlt,
      mk,
      gammaf,
      fck,
      fyk,
      rhoMin,
      distribution,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Flexão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setTShape(jsonData.tShape || false)
        setB(jsonData.b)
        setBf(jsonData.bf)
        setBw(jsonData.bw)
        setH(jsonData.h)
        setHf(jsonData.hf)
        setDlb(jsonData.dlb)
        setDlt(jsonData.dlt)
        setMk(jsonData.mk || jsonData.md || '')
        setGammaf(jsonData.gammaf || 1.4)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setRhoMin(jsonData.rhoMin)
        setDistribution(jsonData.distribution || 'beam')
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const inputImageRef = useRef(null)

  const InputImage = () => {
    return (
      <SvgImage ref={inputImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />
        <rect x="152" y="57" width="178" height="358" stroke="black" strokeWidth="2" />
        <ellipse cx="177.5" cy="389" rx="4.5" ry="4" fill="black" />
        <ellipse cx="177.5" cy="83" rx="4.5" ry="4" fill="black" />
        <ellipse cx="303.5" cy="389" rx="4.5" ry="4" fill="black" />
        <ellipse cx="303.5" cy="83" rx="4.5" ry="4" fill="black" />
        <path
          d="M331 443.205L326 440.318V446.092L331 443.205ZM151 443.205L156 446.092V440.318L151 443.205ZM326.5 443.205V442.705H155.5V443.205V443.705H326.5V443.205Z"
          fill="black"
        />
        <path
          d="M124 416L126.887 411L121.113 411L124 416ZM124 56L121.113 61L126.887 61L124 56ZM124 411.5L124.5 411.5L124.5 60.5L124 60.5L123.5 60.5L123.5 411.5L124 411.5Z"
          fill="black"
        />
        <path
          d="M359 415L361.887 410L356.113 410L359 415ZM359 389L356.113 394L361.887 394L359 389ZM359 410.5L359.5 410.5L359.5 393.5L359 393.5L358.5 393.5L358.5 410.5L359 410.5Z"
          fill="black"
        />
        <path
          d="M359 83L361.887 78L356.113 78L359 83ZM359 57L356.113 62L361.887 62L359 57ZM359 78.5L359.5 78.5L359.5 61.5L359 61.5L358.5 61.5L358.5 78.5L359 78.5Z"
          fill="black"
        />
        <line x1="151.5" y1="423" x2="151.5" y2="454" stroke="black" />
        <line x1="144" y1="56.5" x2="113" y2="56.5" stroke="black" />
        <line x1="373" y1="388.5" x2="314" y2="388.5" stroke="black" />
        <line x1="373" y1="56.5" x2="333" y2="56.5" stroke="black" />
        <line x1="331.5" y1="423" x2="331.5" y2="454" stroke="black" />
        <line x1="144" y1="416.5" x2="113" y2="416.5" stroke="black" />
        <line x1="373" y1="415.5" x2="333" y2="415.5" stroke="black" />
        <line x1="373" y1="83.5" x2="312" y2="83.5" stroke="black" />
        <text
          transform="translate(215 426)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="19.2529" y="13.1604">
            {b !== '' && b != 0 ? b : 'b'}
          </tspan>
        </text>
        <text
          transform="translate(107 288) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="45.6035" y="13">
            {h !== '' && h != 0 ? h : 'h'}
          </tspan>
        </text>
        <text
          transform="translate(380 400) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="0.09375" y="13">
            {dlb !== '' && dlb != 0 ? dlb : "di'"}
          </tspan>
        </text>
        <text
          transform="translate(380 80) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="9.83008" y="13">
            {dlt !== '' && dlt != 0 ? dlt : "ds'"}
          </tspan>
        </text>
      </SvgImage>
    )
  }

  const InputImageT = () => {
    return (
      <SvgImage ref={inputImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />
        <ellipse cx="228.5" cy="384" rx="4.5" ry="4" fill="black" />
        <ellipse cx="228.5" cy="104" rx="4.5" ry="4" fill="black" />
        <ellipse cx="273.5" cy="384" rx="4.5" ry="4" fill="black" />
        <ellipse cx="273.5" cy="104" rx="4.5" ry="4" fill="black" />
        <path
          d="M290 438L285 435.113V440.887L290 438ZM210 438L215 440.887V435.113L210 438ZM285.5 438V437.5H214.5V438V438.5H285.5V438Z"
          fill="black"
        />
        <path
          d="M400 70L395 67.1132V72.8868L400 70ZM100 70L105 72.8868V67.1132L100 70ZM395.5 70V69.5L104.5 69.5V70V70.5L395.5 70.5V70Z"
          fill="black"
        />
        <path
          d="M76 405L78.8868 400L73.1132 400L76 405ZM76 85L73.1133 90L78.8868 90L76 85ZM76 400.5L76.5 400.5L76.5 89.5L76 89.5L75.5 89.5L75.5 400.5L76 400.5Z"
          fill="black"
        />
        <path
          d="M316 405L318.887 400L313.113 400L316 405ZM316 381L313.113 386L318.887 386L316 381ZM316 400.5L316.5 400.5L316.5 385.5L316 385.5L315.5 385.5L315.5 400.5L316 400.5Z"
          fill="black"
        />
        <path
          d="M420 135L422.887 130L417.113 130L420 135ZM420 85L417.113 90L422.887 90L420 85ZM420 130.5L420.5 130.5L420.5 89.5L420 89.5L419.5 89.5L419.5 130.5L420 130.5Z"
          fill="black"
        />
        <path
          d="M310 104L312.887 99L307.113 99L310 104ZM310 85L307.113 90L312.887 90L310 85ZM310 99.5L310.5 99.5L310.5 89.5L310 89.5L309.5 89.5L309.5 99.5L310 99.5Z"
          fill="black"
        />
        <line x1="210.5" y1="418" x2="210.5" y2="449" stroke="black" />
        <line x1="100.5" y1="55" x2="100.5" y2="82" stroke="black" />
        <line x1="96" y1="85.5" x2="65" y2="85.5" stroke="black" />
        <line x1="334" y1="380.5" x2="299" y2="380.5" stroke="black" />
        <line x1="435" y1="85.5" x2="406" y2="85.5" stroke="black" />
        <line x1="290.5" y1="418" x2="290.5" y2="449" stroke="black" />
        <line x1="400.5" y1="55" x2="400.5" y2="82" stroke="black" />
        <line x1="203" y1="405.5" x2="65" y2="405.5" stroke="black" />
        <line x1="334" y1="405.5" x2="299" y2="405.5" stroke="black" />
        <line x1="435" y1="135.5" x2="406" y2="135.5" stroke="black" />
        <line x1="324" y1="103.5" x2="283" y2="103.5" stroke="black" />
        <text
          transform="translate(224 421)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="11.0498" y="13.1604">
            {bw !== '' && bw != 0 ? bw : 'bw'}
          </tspan>
        </text>
        <text
          transform="translate(224 51)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="15.9619" y="13.1604">
            {bf !== '' && bf != 0 ? bf : 'bf'}
          </tspan>
        </text>
        <text
          transform="translate(59 297) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="45.6035" y="13">
            {h !== '' && h != 0 ? h : 'h'}
          </tspan>
        </text>
        <text
          transform="translate(328 405) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="0.09375" y="13">
            {dlb !== '' && dlb != 0 ? dlb : "di'"}
          </tspan>
        </text>
        <text
          transform="translate(429 121) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="1.8125" y="13">
            {hf !== '' && hf != 0 ? hf : 'hf'}
          </tspan>
        </text>
        <text
          transform="translate(330 128) rotate(-90)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="9.83008" y="13">
            {dlt !== '' && dlt != 0 ? dlt : "ds'"}
          </tspan>
        </text>
        <rect x="211" y="134" width="78" height="270" stroke="black" strokeWidth="2" />
        <path d="M399 86V134H101V86H399Z" stroke="black" strokeWidth="2" />
      </SvgImage>
    )
  }

  const handleCopySVG = async (svgRef) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 500
      canvas.height = 500
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  function renderRebarSection(rebars) {
    const dist = result.rebarDistribution || 'beam'

    if (dist === 'mesh') {
      if (!rebars) return <p>Nenhuma tela disponível para a área necessária</p>
      return (
        <p>
          Tela {rebars.name} — {rebars.area} cm²/m (necessário: {roundToDecimals(rebars.requiredAreaPerMeter)} cm²/m)
        </p>
      )
    }

    return (
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Bitola</Th>
              <Th>Área (cm²)</Th>
              <Th>{dist === 'slab' ? 'Espaçamento (cm)' : 'Quantidade'}</Th>
            </tr>
          </thead>
          <tbody>
            {rebars.map((rebar, index) => (
              <Tr key={index}>
                <Td>{rebar.name}</Td>
                <Td>{rebar.area}</Td>
                <Td>{dist === 'slab' ? rebar.spacing : rebar.quantity}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Flexão</span>
          </div>
          <h1>📐 Flexão em Concreto Armado</h1>
          <p className="subtitle">
            Dimensionamento e verificação de seções retangulares e T de concreto armado submetidas à flexão simples
          </p>
          <div className="badges">
            <Badge>⭐ Popular</Badge>
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Domínios 2, 3 e 4</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento completo de seções retangulares ou T de concreto armado à flexão
              simples, considerando os domínios de deformação 2, 3 e 4 conforme a NBR 6118:2014.
            </p>
            <p>
              O cálculo fornece a área de aço necessária, o domínio de deformação da seção e sugere bitolas de armadura
              adequadas para o dimensionamento.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Tabelas de Armadura</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            {tShape ? <InputImageT /> : <InputImage />}
            <CopyImageButton
              type="button"
              title="Clique para copiar essa imagem"
              onClick={() => handleCopySVG(inputImageRef)}
            >
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <RadioInputDiv>
              <span>Formato da seção</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="sectionShape"
                    id="shape-rectangle"
                    value="rectangle"
                    checked={!tShape}
                    onChange={() => setTShape(false)}
                    disabled={isLoading}
                  />
                  <span>Retangular</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="sectionShape"
                    id="shape-t"
                    value="t"
                    checked={tShape}
                    onChange={() => setTShape(true)}
                    disabled={isLoading}
                  />
                  <span>T</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            {!tShape ? (
              <InputDiv>
                <span>Largura (cm)</span>
                <ValidatedInput
                  name="b"
                  type="number"
                  value={b}
                  onChange={(event) => setB(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            ) : (
              <>
                <InputDiv>
                  <span>Largura da mesa (cm)</span>
                  <ValidatedInput
                    name="bf"
                    type="number"
                    value={bf}
                    onChange={(event) => setBf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Largura da alma (cm)</span>
                  <ValidatedInput
                    name="bw"
                    type="number"
                    value={bw}
                    onChange={(event) => setBw(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}
            <InputDiv>
              <span>Altura {tShape ? 'total ' : ''}(cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            {tShape && (
              <InputDiv>
                <span>Altura da mesa (cm)</span>
                <ValidatedInput
                  name="hf"
                  type="number"
                  value={hf}
                  onChange={(event) => setHf(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            <InputDiv>
              <span>Distância da barra tracionada à face tracionada (cm)</span>
              <ValidatedInput
                name="dlb"
                type="number"
                value={dlb}
                onChange={(event) => setDlb(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra comprimida à face comprimida (cm)</span>
              <ValidatedInput
                name="dlt"
                type="number"
                value={dlt}
                onChange={(event) => setDlt(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Momento Característico (kN.cm)</span>
              <ValidatedInput
                name="mk"
                type="number"
                value={mk || ''}
                onChange={(event) => setMk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <InputDiv>
              <span>Taxa de aço mínima (%)</span>
              <ValidatedInput
                name="rhoMin"
                type="number"
                value={rhoMin ?? ''}
                onChange={(event) => setRhoMin(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distribuição da armadura</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={distribution}
                  onChange={(e) => setDistribution(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="beam">Viga (barras)</option>
                  <option value="slab">Laje (espaçamento)</option>
                  <option value="mesh">Tela soldada</option>
                </Select>
              </div>
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
          <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>🔗 Verificações Relacionadas</h3>
          <p style={{ marginBottom: '15px' }}>
            Quer fazer mais verificações para o mesmo elemento? Continue com o dimensionamento:
          </p>
          <ReferenceCard
            to={`/concreto/cisalhamento?b=${tShape ? bw : b}&d=${h - dlb}&fck=${fck}&fyk=${fyk}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>✂️</span>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>Cisalhamento</strong>
              <small style={{ opacity: 0.8 }}>Verificação ao esforço cortante</small>
            </div>
          </ReferenceCard>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              {result.xdratio != 0.45 ? (
                <>
                  <MathDiv>
                    <BlockMath math={`\\dfrac{x}{d}=${result.xdratio}`} />
                    {result.domain == '2' ? (
                      <BlockMath math={`\\dfrac{x}{d}<0,259 \\rightarrow \\text{Domínio } ${result.domain}`} />
                    ) : (
                      <BlockMath math={`\\dfrac{x}{d} \\geq 0,259 \\rightarrow \\text{Domínio } ${result.domain}`} />
                    )}
                  </MathDiv>
                  <MathDiv>
                    <BlockMath math={`A_{s}= ${result.as} \\, \\text{cm}^2`} />
                    {renderRebarSection(result.asRebars)}
                  </MathDiv>
                </>
              ) : (
                <>
                  <MathDiv>
                    <BlockMath
                      math={`x= ${result.x.toFixed(2)} \\, \\text{cm} ${result.xdratio == 0.45 ? ' \\text{ Induzido a } \\dfrac{x}{d} = 0.45' : ''}`}
                    />
                    <h3>Área de aço na região tracionada</h3>
                    <BlockMath math={`A_{s}= ${result.as} \\, \\text{cm}^2`} />
                    {renderRebarSection(result.astRebars)}
                    <h3>Área de aço na região comprimida</h3>
                    <BlockMath math={`A_{sc}=${result.asl} \\, \\text{cm}^2`} />
                    {renderRebarSection(result.ascRebars)}
                  </MathDiv>
                </>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              {!data.tShape || result.flangeOnly ? (
                <>
                  <h3>Posição da linha neutra</h3>
                  <MathDiv>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`x= \\dfrac{0,68 \\cdot d \\pm \\left( \\left( 0,68 \\cdot d \\right) ^2 - \\left( 4 \\cdot 0,272 \\cdot \\dfrac{Md}{${data.tShape ? 'b_f' : 'b'} \\cdot f_{cd}} \\right) \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath
                        math={`x= \\dfrac{0,68 \\cdot ${result.d} \\pm \\left( \\left( 0,68 \\cdot ${result.d} \\right) ^2 - \\left( 4 \\cdot 0,272 \\cdot \\dfrac{${data.md} }{${data.tShape ? data.bf : data.b} \\cdot ${(data.fck / 14).toFixed(3)} } \\right) \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath
                        math={`\\boxed{x= ${result.x} \\, \\text{cm} ${result.xdratio == 0.45 ? ' \\text{ Induzido a } \\dfrac{x}{d} = 0.45' : ''}}`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`x= \\dfrac{A \\pm \\left( A^2 - B \\right)^{\\dfrac{1}{2}}}{0,544} `} />
                      <BlockMath math={`A= 0,68 \\cdot d=0,68 \\cdot ${result.d}=${result.aPart}`} />
                      <BlockMath
                        math={`B= 4 \\cdot 0,272 \\cdot \\dfrac{Md}{${data.tShape ? 'b_f' : 'b'} \\cdot f_{cd}}`}
                      />
                      <BlockMath
                        math={`B= 4 \\cdot 0,272 \\cdot \\dfrac{${data.md}}{${data.tShape ? data.bf : data.b} \\cdot ${(data.fck / 14).toFixed(3)}}`}
                      />
                      <BlockMath math={`B= ${result.bPart}`} />
                      <BlockMath
                        math={`x= \\dfrac{${result.aPart} \\pm \\left( \\left(${result.aPart} \\right)^2 - ${result.bPart} \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath
                        math={`\\boxed{x= ${result.x} \\, \\text{cm} ${result.xdratio == 0.45 ? ' \\text{ Induzido a } \\dfrac{x}{d} = 0.45' : ''}}`}
                      />
                    </MobileOnlyDiv>
                  </MathDiv>
                </>
              ) : (
                <>
                  <h3>Posição da linha neutra</h3>
                  <MathDiv>
                    <h4>Verificação da linha neutra na mesa</h4>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`x_{f}= \\dfrac{0,68 \\cdot d \\pm \\left( \\left( 0,68 \\cdot d \\right) ^2 - \\left( 4 \\cdot 0,272 \\cdot \\dfrac{Md}{b_f \\cdot f_{cd}} \\right) \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath
                        math={`x_{f}= \\dfrac{0,68 \\cdot ${result.d} \\pm \\left( \\left( 0,68 \\cdot ${result.d} \\right) ^2 - \\left( 4 \\cdot 0,272 \\cdot \\dfrac{${data.md} }{${data.bf} \\cdot ${(data.fck / 14).toFixed(3)} } \\right) \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath math={`x_{f}= ${result.flangeX} \\, \\text{cm}`} />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`x_f= \\dfrac{A \\pm \\left( A^2 - B \\right)^{\\dfrac{1}{2}}}{0,544} `} />
                      <BlockMath math={`A= 0,68 \\cdot d=0,68 \\cdot ${result.d}=${result.flangeAPart}`} />
                      <BlockMath math={`B= 4 \\cdot 0,272 \\cdot \\dfrac{Md}{b_f \\cdot f_{cd}}`} />
                      <BlockMath
                        math={`B= 4 \\cdot 0,272 \\cdot \\dfrac{${data.md}}{${data.bf} \\cdot ${(data.fck / 14).toFixed(3)}}`}
                      />
                      <BlockMath math={`B= ${result.flangeBPart}`} />
                      <BlockMath math={`x_f= ${result.flangeX} \\, \\text{cm}`} />
                    </MobileOnlyDiv>
                    <BlockMath
                      math={`x_f=${result.flangeX} > h_f=${data.hf} \\rightarrow \\text{Linha neutra na alma}`}
                    />
                    <h4>Momento resistido pela mesa</h4>
                    <BlockMath math={`z_1=d-\\dfrac{h_f}{2}`} />
                    <BlockMath math={`z_1=${result.d}-\\dfrac{${data.hf}}{2}=${result.z1} \\, \\text{cm}`} />
                    <BlockMath math={`M_1=0,85\\cdot f_{cd} \\cdot h_f \\cdot (b_f - b_w) \\cdot z_1`} />
                    <BlockMath
                      math={`M_1=0,85\\cdot \\dfrac{${data.fck / 10}}{1,4} \\cdot ${data.hf} \\cdot (${data.bf} - ${data.bw}) \\cdot ${result.z1}`}
                    />
                    <BlockMath math={`M_1=${result.m1} \\, \\text{kN}\\cdot\\text{cm}`} />
                    <h4>Momento resistido pela alma</h4>
                    <BlockMath math={`M_2=M_d-M_1=${data.md}-${result.m1}`} />
                    <BlockMath math={`M_2=${result.m2} \\, \\text{kN}\\cdot\\text{cm}`} />
                    <h4>Posição da linha neutra na alma</h4>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`x= \\dfrac{0,68 \\cdot d \\pm \\left( \\left( 0,68 \\cdot d \\right) ^2 - \\left( 4 \\cdot 0,272 \\cdot \\dfrac{M_2}{b_w \\cdot f_{cd}} \\right) \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath
                        math={`x= \\dfrac{0,68 \\cdot ${result.d} \\pm \\left( \\left( 0,68 \\cdot ${result.d} \\right) ^2 - \\left( 4 \\cdot 0,272 \\cdot \\dfrac{${result.m2} }{${data.bw} \\cdot ${(data.fck / 14).toFixed(3)} } \\right) \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath math={`\\boxed{x= ${result.x} \\, \\text{cm}}`} />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`x= \\dfrac{A \\pm \\left( A^2 - B \\right)^{\\dfrac{1}{2}}}{0,544} `} />
                      <BlockMath math={`A= 0,68 \\cdot d=0,68 \\cdot ${result.d}=${result.aPart}`} />
                      <BlockMath math={`B= 4 \\cdot 0,272 \\cdot \\dfrac{M_2}{b_w \\cdot f_{cd}}`} />
                      <BlockMath
                        math={`B= 4 \\cdot 0,272 \\cdot \\dfrac{${result.m2}}{${data.bw} \\cdot ${(data.fck / 14).toFixed(3)}}`}
                      />
                      <BlockMath math={`B= ${result.bPart}`} />
                      <BlockMath
                        math={`x= \\dfrac{${result.aPart} \\pm \\left( \\left(${result.aPart} \\right)^2 - ${result.bPart} \\right)^{\\dfrac{1}{2}}}{0,544} `}
                      />
                      <BlockMath math={`\\boxed{x= ${result.x} \\, \\text{cm}}`} />
                    </MobileOnlyDiv>
                  </MathDiv>
                </>
              )}
              <h3>Domínio</h3>
              <MathDiv>
                <BlockMath math={`\\dfrac{x}{d}=\\dfrac{${result.x}}{${result.d}}=${result.xdratio}`} />
                {result.domain == '2' ? (
                  <BlockMath math={`\\dfrac{x}{d}<0,259 \\rightarrow \\text{Domínio } ${result.domain}`} />
                ) : (
                  <BlockMath math={`\\dfrac{x}{d} \\geq 0,259 \\rightarrow \\text{Domínio } ${result.domain}`} />
                )}
              </MathDiv>
              <h3>Cálculo da armação</h3>
              <MathDiv>
                {!data.tShape ? (
                  result.xdratio != 0.45 ? (
                    <>
                      <BlockMath math={`z=d-0,4\\cdot x`} />
                      <BlockMath math={`z=${result.d}-0,4\\cdot ${result.x}=${result.z} \\, \\text{cm}`} />
                      <BlockMath
                        math={`A_s=\\dfrac{M_d}{z\\cdot f_{yd}}=\\dfrac{${data.md}}{${result.z}\\cdot ${result.fyd}}`}
                      />
                      <BlockMath math={`A_{s,calc}=${result.ascalc.toFixed(2)} \\, \\text{cm}^2`} />
                      <BlockMath math={`A_{s,min}=\\rho_{min} \\cdot b \\cdot h`} />
                      <BlockMath
                        math={`A_{s,min}= \\dfrac{${data.rhoMin}}{100} \\cdot ${data.b} \\cdot ${data.h} = ${result.asmin} \\, \\text{cm}^2`}
                      />
                      <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.asRebars)}
                    </>
                  ) : (
                    <>
                      <h4>Força no concreto</h4>
                      <BlockMath math={`F_c=0,85\\cdot f_{cd} \\cdot b \\cdot 0,8 \\cdot x`} />
                      <BlockMath
                        math={`F_c=0,85\\cdot \\dfrac{${data.fck / 10}}{1.4} \\cdot ${data.b} \\cdot 0,8 \\cdot ${result.x}`}
                      />
                      <BlockMath math={`F_c=${result.fc} \\, \\text{kN}`} />
                      <h4>Momento para o limite de x</h4>
                      <BlockMath math={`z=d - 0,4\\cdot x`} />
                      <BlockMath math={`z=${result.d} - 0,4\\cdot ${result.x}=${result.z} \\, \\text{cm}`} />
                      <BlockMath math={`M_1=F_c \\cdot z=${result.fc} \\cdot ${result.z}`} />
                      <BlockMath math={`M_1=${result.m1} \\, \\text{kN}\\cdot\\text{cm}`} />
                      <h4>Área de aço para o limite de x</h4>
                      <BlockMath
                        math={`A_{s1}=\\dfrac{M_1}{z \\cdot f_{yd}}=\\dfrac{${result.m1}}{${result.z} \\cdot ${result.fyd}}`}
                      />
                      <BlockMath math={`A_{s1}=${result.asm1} \\, \\text{cm}^2`} />
                      <h4>Momento excedente</h4>
                      <BlockMath math={`M_2=M_d-M_1=${data.md}-${result.m1}`} />
                      <BlockMath math={`M_2=${result.m2} \\, \\text{kN}\\cdot\\text{cm}`} />
                      <h4>Área de aço para o momento excedente</h4>
                      <BlockMath
                        math={`A_{s2}=\\dfrac{M_2}{(d-d'_s) \\cdot f_{yd}}=\\dfrac{${result.m2}}{(${result.d} - ${data.dlt} ) \\cdot ${result.fyd}}`}
                      />
                      <BlockMath math={`A_{s2}=${result.asm2} \\, \\text{cm}^2`} />
                      <h4>Área de aço na região tracionada</h4>
                      <BlockMath math={`A_{st}=A_{s1} + A_{s2}=${result.asm1}+${result.asm2}`} />
                      <BlockMath math={`A_{st}=${result.ascalc.toFixed(2)} \\, \\text{cm}^2`} />
                      <BlockMath math={`A_{s,min}=\\rho_{min} \\cdot b \\cdot h`} />
                      <BlockMath
                        math={`A_{s,min}= \\dfrac{${data.rhoMin}}{100} \\cdot ${data.b} \\cdot ${data.h} = ${result.asmin} \\, \\text{cm}^2`}
                      />
                      <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.astRebars)}
                      <h4>Deformação na barra comprimida</h4>
                      <BlockMath math={`\\epsilon=\\dfrac{0,0035 \\cdot (x - d'_s)}{x}`} />
                      <BlockMath math={`\\epsilon=\\dfrac{0,0035 \\cdot (${result.x} - ${data.dlt})}{${result.x}}`} />
                      <BlockMath math={`\\epsilon=${result.strain}`} />
                      <h4>Tensão na barra comprimida</h4>
                      <BlockMath>{String.raw`\sigma \leq \begin{cases} 21.000 \cdot \epsilon \\ f_{yd} \end{cases}=${result.stress} \, \text{kN/cm}^2`}</BlockMath>
                      <h4>Área de aço comprimida</h4>
                      <BlockMath
                        math={`A_{sc}=A_{s2} \\cdot \\dfrac{\\sigma}{f_{yd}}=${result.asm2} \\cdot \\dfrac{${result.stress}}{${result.fyd}}`}
                      />
                      <BlockMath math={`\\boxed{A_{sc}=${result.asl} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.ascRebars)}
                    </>
                  )
                ) : result.flangeOnly ? (
                  result.xdratio != 0.45 ? (
                    <>
                      <BlockMath math={`z=d-0,4\\cdot x`} />
                      <BlockMath math={`z=${result.d}-0,4\\cdot ${result.x}=${result.z} \\, \\text{cm}`} />
                      <BlockMath
                        math={`A_s=\\dfrac{M_d}{z\\cdot f_{yd}}=\\dfrac{${data.md}}{${result.z}\\cdot ${result.fyd}}`}
                      />
                      <BlockMath math={`A_{s,calc}=${result.ascalc.toFixed(2)} \\, \\text{cm}^2`} />
                      <BlockMath math={`A_{s,min}=\\rho_{min} \\cdot A_c`} />
                      <BlockMath math={`A_c=b_f \\cdot h_f + b_w \\cdot (h-h_f)`} />
                      <BlockMath
                        math={`A_c=${data.bf} \\cdot ${data.hf} + ${data.bw} \\cdot (${data.h}-${data.hf})=${result.concreteArea.toFixed(2)} \\, \\text{cm}^2`}
                      />
                      <BlockMath
                        math={`A_{s,min}= \\dfrac{${data.rhoMin}}{100} \\cdot ${result.concreteArea.toFixed(2)} = ${result.asmin} \\, \\text{cm}^2`}
                      />
                      <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.asRebars)}
                    </>
                  ) : (
                    <>
                      <h4>Força no concreto</h4>
                      <BlockMath math={`F_c=0,85\\cdot f_{cd} \\cdot b_f \\cdot 0,8 \\cdot x`} />
                      <BlockMath
                        math={`F_c=0,85\\cdot \\dfrac{${data.fck / 10}}{1.4} \\cdot ${data.bf} \\cdot 0,8 \\cdot ${result.x}`}
                      />
                      <BlockMath math={`F_c=${result.fc} \\, \\text{kN}`} />
                      <h4>Momento para o limite de x</h4>
                      <BlockMath math={`z=d - 0,4\\cdot x`} />
                      <BlockMath math={`z=${result.d} - 0,4\\cdot ${result.x}=${result.z} \\, \\text{cm}`} />
                      <BlockMath math={`M_1=F_c \\cdot z=${result.fc} \\cdot ${result.z}`} />
                      <BlockMath math={`M_1=${result.m1} \\, \\text{kN}\\cdot\\text{cm}`} />
                      <h4>Área de aço para o limite de x</h4>
                      <BlockMath
                        math={`A_{s1}=\\dfrac{M_1}{z \\cdot f_{yd}}=\\dfrac{${result.m1}}{${result.z} \\cdot ${result.fyd}}`}
                      />
                      <BlockMath math={`A_{s1}=${result.asm1} \\, \\text{cm}^2`} />
                      <h4>Momento excedente</h4>
                      <BlockMath math={`M_2=M_d-M_1=${data.md}-${result.m1}`} />
                      <BlockMath math={`M_2=${result.m2} \\, \\text{kN}\\cdot\\text{cm}`} />
                      <h4>Área de aço para o momento excedente</h4>
                      <BlockMath
                        math={`A_{s2}=\\dfrac{M_2}{(d-d'_s) \\cdot f_{yd}}=\\dfrac{${result.m2}}{(${result.d} - ${data.dlt} ) \\cdot ${result.fyd}}`}
                      />
                      <BlockMath math={`A_{s2}=${result.asm2} \\, \\text{cm}^2`} />
                      <h4>Área de aço na região tracionada</h4>
                      <BlockMath math={`A_{st}=A_{s1} + A_{s2}=${result.asm1}+${result.asm2}`} />
                      <BlockMath math={`A_{st}=${result.ascalc.toFixed(2)} \\, \\text{cm}^2`} />
                      <BlockMath math={`A_{s,min}=\\rho_{min} \\cdot A_c`} />
                      <BlockMath math={`A_c=b_f \\cdot h_f + b_w \\cdot (h-h_f)`} />
                      <BlockMath
                        math={`A_c=${data.bf} \\cdot ${data.hf} + ${data.bw} \\cdot (${data.h}-${data.hf})=${result.concreteArea.toFixed(2)} \\, \\text{cm}^2`}
                      />
                      <BlockMath
                        math={`A_{s,min}= \\dfrac{${data.rhoMin}}{100} \\cdot ${result.concreteArea.toFixed(2)} = ${result.asmin} \\, \\text{cm}^2`}
                      />
                      <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.astRebars)}
                      <h4>Deformação na barra comprimida</h4>
                      <BlockMath math={`\\epsilon=\\dfrac{0,0035 \\cdot (x - d'_s)}{x}`} />
                      <BlockMath math={`\\epsilon=\\dfrac{0,0035 \\cdot (${result.x} - ${data.dlt})}{${result.x}}`} />
                      <BlockMath math={`\\epsilon=${result.strain}`} />
                      <h4>Tensão na barra comprimida</h4>
                      <BlockMath>{String.raw`\sigma \leq \begin{cases} 21.000 \cdot \epsilon \\ f_{yd} \end{cases}=${result.stress} \, \text{kN/cm}^2`}</BlockMath>
                      <h4>Área de aço comprimida</h4>
                      <BlockMath
                        math={`A_{sc}=A_{s2} \\cdot \\dfrac{\\sigma}{f_{yd}}=${result.asm2} \\cdot \\dfrac{${result.stress}}{${result.fyd}}`}
                      />
                      <BlockMath math={`\\boxed{A_{sc}=${result.asl} \\, \\text{cm}^2}`} />
                      {renderRebarSection(result.ascRebars)}
                    </>
                  )
                ) : (
                  <>
                    <BlockMath math={`z=d-0,4\\cdot x`} />
                    <BlockMath math={`z=${result.d}-0,4\\cdot ${result.x}=${result.z} \\, \\text{cm}`} />
                    <BlockMath
                      math={`A_{s1}=\\dfrac{M_1}{z_1\\cdot f_{yd}}=\\dfrac{${result.m1}}{${result.z1}\\cdot ${result.fyd}}`}
                    />
                    <BlockMath math={`A_{s1}=${result.as1} \\, \\text{cm}^2`} />
                    <BlockMath
                      math={`A_{s2}=\\dfrac{M_2}{z\\cdot f_{yd}}=\\dfrac{${result.m2}}{${result.z}\\cdot ${result.fyd}}`}
                    />
                    <BlockMath math={`A_{s2}=${result.as2} \\, \\text{cm}^2`} />
                    <BlockMath
                      math={`A_{s,calc}=A_{s1}+A_{s2}=${result.as1}+${result.as2}=${result.ascalc.toFixed(2)} \\, \\text{cm}^2`}
                    />
                    <BlockMath math={`A_{s,min}=\\rho_{min} \\cdot A_c`} />
                    <BlockMath math={`A_c=b_f \\cdot h_f + b_w \\cdot (h-h_f)`} />
                    <BlockMath
                      math={`A_c=${data.bf} \\cdot ${data.hf} + ${data.bw} \\cdot (${data.h}-${data.hf})=${result.concreteArea.toFixed(2)} \\, \\text{cm}^2`}
                    />
                    <BlockMath
                      math={`A_{s,min}= \\dfrac{${data.rhoMin}}{100} \\cdot ${result.concreteArea.toFixed(2)} = ${result.asmin} \\, \\text{cm}^2`}
                    />
                    <BlockMath math={`\\boxed{A_{s}= ${result.as} \\, \\text{cm}^2}`} />
                    {renderRebarSection(result.asRebars)}
                  </>
                )}
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
